import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  AuthLayout,
  Banner,
  BrandMark,
  Button,
  FormField,
  Heading,
  Link,
  Spinner,
  Stack,
  Surface,
  Text,
  TextInput,
} from '../ui';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';

/**
 * Register / Sign in (§14 auth). react-hook-form + zod resolver on the `@/ui` design
 * layer (FormField wires label/help/error + aria; TextInput is the control). On success
 * the ApiClient stores the token, the auth context refreshes `me`, and we navigate into
 * the app.
 *
 * #334: when Google sign-in is configured it is the ONLY path shown — the email/password
 * form and the login/signup toggle are hidden. Password auth stays fully functional on the
 * SERVER (routes untouched); the form is kept purely as a FALLBACK that renders only when
 * Google is unconfigured (or its status probe fails), so a deployment is never locked out
 * and the E2E suite — which drives the password form — keeps working.
 */

const registerSchema = z.object({
  name: z.string().min(1, 'Required'),
  workspaceName: z.string().min(1, 'Required'),
  email: z.string().email('Enter a valid email'),
  password: z.string().min(8, 'At least 8 characters'),
  // #58: optional signup code. Enforced server-side only when configured; the field
  // is always shown so a deployment with a code can accept it. Empty is allowed here
  // (the server 403s if a code is required and this is blank/wrong).
  signupCode: z.string().optional(),
});
const loginSchema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(1, 'Required'),
});

type Mode = 'register' | 'login';

/**
 * #52: signup is restricted to approved email domains server-side. We surface the
 * list as a static helper line on the register form (the server config is the
 * source of truth + enforcer; this is UX only). Keep in sync with the default of
 * `SIGNUP_ALLOWED_DOMAINS` in the API config.
 */
const ALLOWED_SIGNUP_DOMAINS = ['ehe.ai', 'thestartupfactory.tech', 'tsf.tech'];

export function AuthPage({ mode }: { mode: Mode }) {
  const navigate = useNavigate();
  const { onAuthenticated } = useAuth();
  const [serverError, setServerError] = useState<string | null>(null);

  const isRegister = mode === 'register';
  const form = useForm({
    resolver: zodResolver(isRegister ? registerSchema : loginSchema),
    defaultValues: isRegister
      ? { name: '', workspaceName: '', email: '', password: '', signupCode: '' }
      : { email: '', password: '' },
  });

  // #56: is "Sign in with Google" available? A 503-free status probe drives whether
  // the button renders. #334: it also gates the whole page — when Google is configured
  // it is the ONLY sign-in path; the password form shows only as a fallback below.
  const googleStatus = useQuery({
    queryKey: ['google-login-status'],
    queryFn: () => api.googleLoginStatus(),
    staleTime: 5 * 60_000,
    retry: false,
  });
  const googleConfigured = googleStatus.data?.configured === true;
  const [googleError, setGoogleError] = useState<string | null>(null);

  // While the probe is in flight we show a spinner rather than flashing the password form
  // (prod has Google configured, so the form would appear then vanish). The email/password
  // fallback renders ONLY once we know Google is unavailable (resolved-unconfigured, or the
  // probe errored — fail open so no one is locked out).
  const googleLoading = googleStatus.isPending;
  const passwordAuth = !googleLoading && !googleConfigured;

  const startGoogle = async (): Promise<void> => {
    setGoogleError(null);
    try {
      const { authorizeUrl } = await api.startGoogleLogin();
      // Full-page redirect to Google's consent screen; it returns to
      // /oauth/google/login-callback which exchanges the code and stores the session.
      window.location.assign(authorizeUrl);
    } catch (err) {
      setGoogleError(
        err instanceof ApiError ? err.message : 'Could not start Google sign-in. Try again.',
      );
    }
  };

  const submit = form.handleSubmit(async (values) => {
    setServerError(null);
    try {
      if (isRegister) {
        await api.register(values as z.infer<typeof registerSchema>);
      } else {
        await api.login(values as z.infer<typeof loginSchema>);
      }
      await onAuthenticated();
      await navigate({ to: '/app/dashboard' });
    } catch (err) {
      // #52: a domain-restricted signup comes back as 422 with the message keyed
      // under `email`. Surface it cleanly under the email field rather than as a
      // generic banner. Any other ApiError falls back to the banner.
      const detailEmail =
        err instanceof ApiError && err.details && typeof err.details === 'object'
          ? (err.details as Record<string, unknown>).email
          : undefined;
      const emailDetail =
        Array.isArray(detailEmail) && typeof detailEmail[0] === 'string' ? detailEmail[0] : null;
      if (emailDetail) {
        form.setError('email', { type: 'server', message: emailDetail });
      } else {
        setServerError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
      }
    }
  });

  const errors = form.formState.errors;
  // `workspaceName`/`signupCode` exist only on the register schema — narrow before reading.
  const workspaceNameError = 'workspaceName' in errors ? errors.workspaceName?.message : undefined;
  const signupCodeError = 'signupCode' in errors ? errors.signupCode?.message : undefined;

  return (
    <AuthLayout maxWidth="24rem">
      <Stack gap="6">
        <Stack gap="4">
          <BrandMark />
          <Stack gap="1">
            <Heading level={1} size="xl">
              {isRegister ? 'Create your account' : 'Welcome back'}
            </Heading>
            <Text tone="secondary">
              {isRegister ? 'Create your account and workspace' : 'Sign in to your workspace'}
            </Text>
          </Stack>
        </Stack>

        <Surface tone="default" bordered radius="lg" elevation="sm" padding="6">
          <Stack gap="5">
            {googleLoading ? (
              <Stack gap="2" align="center">
                <Spinner size="sm" />
                <Text size="sm" tone="muted">
                  Loading…
                </Text>
              </Stack>
            ) : googleConfigured ? (
              /* #334: Google is the ONLY sign-in path shown when configured. */
              <Stack gap="3">
                {googleError && (
                  <Banner tone="danger" role="alert">
                    {googleError}
                  </Banner>
                )}
                <Button
                  variant="primary"
                  size="lg"
                  fullWidth
                  onClick={() => void startGoogle()}
                  data-testid="google-signin"
                >
                  {isRegister ? 'Sign up with Google' : 'Sign in with Google'}
                </Button>
              </Stack>
            ) : (
              /* Fallback: Google unconfigured (or its probe failed). The password form keeps
                 the deployment usable and is what the E2E suite drives. Server routes for
                 password auth are always available regardless of this UI. */
              <form onSubmit={submit} noValidate>
                <Stack gap="4">
                  {serverError && (
                    <Banner tone="danger" role="alert">
                      {serverError}
                    </Banner>
                  )}
                  {isRegister && (
                    <>
                      <FormField label="Your name" error={errors.name?.message}>
                        <TextInput
                          autoComplete="name"
                          {...form.register('name')}
                          data-testid="name"
                        />
                      </FormField>
                      <FormField label="Workspace name" error={workspaceNameError}>
                        <TextInput {...form.register('workspaceName')} data-testid="workspaceName" />
                      </FormField>
                    </>
                  )}
                  <FormField
                    label="Email"
                    error={errors.email?.message}
                    help={
                      isRegister
                        ? `Sign-up is restricted to: ${ALLOWED_SIGNUP_DOMAINS.join(', ')}`
                        : undefined
                    }
                    helpTestId="email-helper"
                  >
                    <TextInput
                      type="email"
                      autoComplete="email"
                      {...form.register('email')}
                      data-testid="email"
                    />
                  </FormField>
                  <FormField label="Password" error={errors.password?.message}>
                    <TextInput
                      type="password"
                      autoComplete={isRegister ? 'new-password' : 'current-password'}
                      {...form.register('password')}
                      data-testid="password"
                    />
                  </FormField>
                  {isRegister && (
                    <FormField
                      label="Signup code (if required)"
                      error={signupCodeError}
                      help="Enter the code your workspace provided, if your organisation requires one."
                    >
                      <TextInput
                        autoComplete="off"
                        {...form.register('signupCode')}
                        data-testid="signupCode"
                      />
                    </FormField>
                  )}
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    fullWidth
                    loading={form.formState.isSubmitting}
                    data-testid="submit"
                  >
                    {isRegister ? 'Create account' : 'Sign in'}
                  </Button>
                </Stack>
              </form>
            )}
          </Stack>
        </Surface>

        {/* #334: the login/signup toggle is only relevant to the password fallback — a single
            "Sign in with Google" button handles both sign-in and first-time sign-up. */}
        {passwordAuth && (
          <Text tone="secondary" size="sm" align="center">
            {isRegister ? (
              <>
                Already have an account?{' '}
                <Link as="button" type="button" onClick={() => void navigate({ to: '/login' })}>
                  Sign in
                </Link>
              </>
            ) : (
              <>
                New here?{' '}
                <Link as="button" type="button" onClick={() => void navigate({ to: '/register' })}>
                  Create an account
                </Link>
              </>
            )}
          </Text>
        )}
      </Stack>
    </AuthLayout>
  );
}
